import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import DossiersTabs from "@/components/DossiersTabs";
import { api, formatEUR, formatEURCompact, getStatusPill, formatDate } from "@/lib/api";
import { CurrencyEur, TrendUp, Buildings } from "@phosphor-icons/react";
import { toast } from "sonner";

function YearSelect({ value, onChange }) {
  const now = new Date().getFullYear();
  const years = [now, now - 1, now - 2];
  return (
    <div className="segmented" data-testid="year-selector">
      <button data-active={!value} onClick={() => onChange(null)} data-testid="year-all">Tout</button>
      {years.map((y) => (
        <button key={y} data-active={value === y} onClick={() => onChange(y)} data-testid={`year-${y}`}>{y}</button>
      ))}
    </div>
  );
}

export default function DossiersSignes() {
  const [data, setData] = useState(null);
  const [year, setYear] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const params = year ? `?year=${year}` : "";
      const res = await api.get(`/dossiers-signes${params}`);
      setData(res.data);
    } catch { toast.error("Erreur"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [year]);

  return (
    <Layout title="Dossiers signés" centerTitle>
      <button onClick={() => navigate("/")} className="hidden md:inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-5 font-semibold">
        ← Retour
      </button>
      <div className="hidden md:block mb-5">
        <div className="overline">Archive</div>
        <h1 className="font-display text-4xl font-extrabold text-slate-900">Dossiers signés</h1>
      </div>

      <DossiersTabs activeKey="signes" />

      <div className="mb-5"><YearSelect value={year} onChange={setYear} /></div>

      {/* Totaux */}
      {data && (
        <div className="grid grid-cols-3 gap-3 mb-5" data-testid="signes-totals">
          <div className="card-base p-4">
            <div className="overline">Dossiers</div>
            <div className="metric-value text-3xl text-[color:var(--cf-success)]">{data.totals.count}</div>
          </div>
          <div className="card-base p-4">
            <div className="overline">CA brut</div>
            <div className="metric-value text-xl md:text-2xl text-slate-900">{formatEURCompact(data.totals.ca_brut)}</div>
          </div>
          <div className="card-base p-4">
            <div className="overline">CA net</div>
            <div className="metric-value text-xl md:text-2xl text-[color:var(--cf-success)]">{formatEURCompact(data.totals.ca_net)}</div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="card-base h-28 animate-pulse" />)}</div>
      ) : !data?.items?.length ? (
        <div className="card-base p-8 text-center text-slate-500" data-testid="signes-empty">
          Aucun dossier signé {year ? `en ${year}` : ""}.
        </div>
      ) : (
        <div className="space-y-3" data-testid="signes-list">
          {data.items.map((d) => (
            <button key={d.id} onClick={() => navigate(`/dossier/${d.id}`)} className="card-base p-4 md:p-5 w-full text-left" data-testid={`signe-${d.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-display text-base font-extrabold text-slate-900 truncate">{d.nom}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{d.client_name}</div>
                  {d.banques?.length > 0 && (
                    <div className="text-[11px] text-slate-400 mt-1 flex items-center gap-1">
                      <Buildings size={11} /> {d.banques.join(" / ")}
                    </div>
                  )}
                </div>
                <span className="pill pill-green text-[11px]">Signé</span>
              </div>
              <div className="mt-3 flex items-center justify-between">
                <div className="flex items-center gap-3 text-[12px] text-slate-500">
                  <span>Signé le {formatDate(d.etape_dates?.acte_authentique)}</span>
                </div>
                <div className="flex items-center gap-1.5 bg-[color:var(--cf-success-soft)] px-3 py-1.5 rounded-lg">
                  <TrendUp size={14} color="var(--cf-success)" weight="bold" />
                  <span className="metric-value text-base text-[color:var(--cf-success)]">{formatEUR(d.ca_net)}</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </Layout>
  );
}
